import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { 
  FaUtensils, 
  FaUsers, 
  FaChartLine, 
  FaMobileAlt, 
  FaShieldAlt, 
  FaClock,
  FaStar,
  FaArrowRight,
  FaCheckCircle,
  FaPlay,
  FaHeart,
  FaRocket,
  FaGlobe,
  FaAward,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaBars,
  FaTimes,
  FaChevronDown,
  FaHome,
  FaInfoCircle,
  FaCog,
  FaQuestionCircle
} from 'react-icons/fa';

const LandingPage = () => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      icon: <FaUtensils className="w-10 h-10" />,
      title: "Menu Management",
      description: "Create, edit, and manage your catering menus with ease. Organize items by categories and track availability.",
      gradient: "from-primary to-primary/80"
    },
    {
      icon: <FaUsers className="w-10 h-10" />,
      title: "Order Management", 
      description: "Streamline your order process from creation to delivery. Track orders, manage customer details, and handle payments.",
      gradient: "from-primary/90 to-secondary"
    },
    {
      icon: <FaChartLine className="w-10 h-10" />,
      title: "Analytics & Reports",
      description: "Get insights into your business performance with detailed reports and analytics dashboard.",
      gradient: "from-primary to-primary/70"
    },
    {
      icon: <FaMobileAlt className="w-10 h-10" />,
      title: "Mobile Responsive",
      description: "Access your catering management system from any device, anywhere, anytime.",
      gradient: "from-secondary to-primary"
    },
    {
      icon: <FaShieldAlt className="w-10 h-10" />,
      title: "Role-Based Access",
      description: "Secure your business with comprehensive role management and permission controls.",
      gradient: "from-primary/80 to-secondary/80"
    },
    {
      icon: <FaClock className="w-10 h-10" />,
      title: "Real-time Updates",
      description: "Stay updated with real-time notifications and instant synchronization across all devices.",
      gradient: "from-secondary to-primary/90"
    }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "Catering Business Owner",
      content: "This system has revolutionized how we manage our catering business. The order tracking and menu management features are incredible!",
      rating: 5,
      avatar: "SJ"
    },
    {
      name: "Michael Chen", 
      role: "Event Coordinator",
      content: "The analytics dashboard gives us insights we never had before. We can now make data-driven decisions for our business growth.",
      rating: 5,
      avatar: "MC"
    },
    {
      name: "Emily Rodriguez",
      role: "Restaurant Manager", 
      content: "The mobile responsiveness is outstanding. I can manage orders and check inventory while on the go. Highly recommended!",
      rating: 5,
      avatar: "ER"
    }
  ];

  const stats = [
    { number: "500+", label: "Active Caterers", icon: <FaUsers className="w-6 h-6" />, gradient: "from-primary to-primary/80" },
    { number: "10K+", label: "Orders Processed", icon: <FaUtensils className="w-6 h-6" />, gradient: "from-primary/90 to-secondary" },
    { number: "99.9%", label: "Uptime", icon: <FaAward className="w-6 h-6" />, gradient: "from-secondary to-primary" },
    { number: "24/7", label: "Support", icon: <FaHeart className="w-6 h-6" />, gradient: "from-primary to-secondary/80" }
  ];

  const menuItems = [
    { name: "Home", href: "#home", icon: <FaHome className="w-4 h-4" /> },
    { name: "Features", href: "#features", icon: <FaRocket className="w-4 h-4" /> },
    { name: "About", href: "#about", icon: <FaInfoCircle className="w-4 h-4" /> },
    { name: "Pricing", href: "#pricing", icon: <FaAward className="w-4 h-4" /> },
    { name: "Contact", href: "#contact", icon: <FaEnvelope className="w-4 h-4" /> }
  ];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Unique Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-xl shadow-2xl border-b border-primary/10' 
          : 'bg-transparent'
      }`}>
        {/* Top Bar */}
        <div className="bg-gradient-to-r from-primary to-secondary text-white py-2">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center space-x-6">
                <div className="flex items-center space-x-2">
                  <FaPhone className="w-3 h-3" />
                  <span>+1 (555) 123-4567</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FaEnvelope className="w-3 h-3" />
                  <span>support@catererspro.com</span>
                </div>
              </div>
              <div className="hidden md:flex items-center space-x-4">
                <span className="text-xs">Free 14-day trial • No credit card required</span>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  <span className="text-xs">Live Support</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Navigation */}
        <nav className="bg-white/90 backdrop-blur-md">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-20">
              {/* Logo */}
              <div className="flex items-center group">
                <div className="relative">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-2xl flex items-center justify-center mr-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <FaUtensils className="w-7 h-7 text-white" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                    <span className="text-xs font-bold text-white">Pro</span>
                  </div>
                </div>
                <div>
                  <span className="text-2xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                    Caterers Pro
                  </span>
                  <div className="text-xs text-gray-500 -mt-1">Professional Management</div>
                </div>
              </div>

              {/* Desktop Navigation */}
              <div className="hidden lg:flex items-center space-x-8">
                {menuItems.map((item, index) => (
                  <a
                    key={index}
                    href={item.href}
                    className="flex items-center space-x-2 text-gray-700 hover:text-primary transition-all duration-300 group relative"
                  >
                    <span className="group-hover:scale-110 transition-transform duration-300">{item.icon}</span>
                    <span className="font-medium">{item.name}</span>
                    <div className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-primary to-secondary group-hover:w-full transition-all duration-300"></div>
                  </a>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="hidden lg:flex items-center space-x-4">
                <Link 
                  to="/login" 
                  className="text-gray-700 hover:text-primary px-4 py-2 rounded-lg font-medium transition-all duration-300 hover:bg-primary/10"
                >
                  Login
                </Link>
                <Link 
                  to="/register" 
                  className="bg-gradient-to-r from-primary to-secondary text-white px-6 py-3 rounded-xl font-semibold hover:from-primary/90 hover:to-secondary/90 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center space-x-2"
                >
                  <FaRocket className="w-4 h-4" />
                  <span>Get Started</span>
                </Link>
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="lg:hidden p-2 rounded-lg text-gray-700 hover:text-primary hover:bg-primary/10 transition-all duration-300"
              >
                {isMenuOpen ? <FaTimes className="w-6 h-6" /> : <FaBars className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-gray-200 shadow-xl">
              <div className="px-4 py-6 space-y-4">
                {menuItems.map((item, index) => (
                  <a
                    key={index}
                    href={item.href}
                    className="flex items-center space-x-3 text-gray-700 hover:text-primary transition-all duration-300 py-2 px-3 rounded-lg hover:bg-primary/10"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <span>{item.icon}</span>
                    <span className="font-medium">{item.name}</span>
                  </a>
                ))}
                <div className="pt-4 border-t border-gray-200 space-y-3">
                  <Link 
                    to="/login" 
                    className="block w-full text-center text-gray-700 hover:text-primary py-3 px-4 rounded-lg font-medium transition-all duration-300 hover:bg-primary/10"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Login
                  </Link>
                  <Link 
                    to="/register" 
                    className="block w-full text-center bg-gradient-to-r from-primary to-secondary text-white py-3 px-4 rounded-xl font-semibold hover:from-primary/90 hover:to-secondary/90 transition-all duration-300"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Get Started
                  </Link>
                </div>
              </div>
            </div>
          )}
        </nav>

        {/* Floating Notification */}
        <div className="absolute top-24 right-4 bg-white/90 backdrop-blur-sm rounded-xl shadow-lg p-3 border border-primary/20 animate-bounce">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
            <span className="text-xs font-medium text-gray-700">New features available!</span>
            <button className="text-gray-400 hover:text-gray-600">
              <FaTimes className="w-3 h-3" />
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center bg-gradient-to-br from-primary/5 via-white to-secondary/5 overflow-hidden pt-32">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-full opacity-30 animate-pulse"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-secondary/20 to-primary/20 rounded-full opacity-30 animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-full opacity-20 animate-pulse delay-500"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-6">
                <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-full text-sm font-medium text-primary border border-primary/20">
                  <FaRocket className="w-4 h-4 mr-2" />
                  Professional Catering Management
                </div>
                <h1 className="text-5xl lg:text-7xl font-bold leading-tight">
                  <span className="text-gray-900">Professional</span>
                  <span className="block bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">Catering Management</span>
                  <span className="text-gray-600 text-4xl lg:text-5xl">Made Simple</span>
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed max-w-2xl">
                  Streamline your catering business with our comprehensive management system. 
                  Manage orders, menus, inventory, and customers all in one beautiful, intuitive platform.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/register"
                  className="group bg-gradient-to-r from-primary to-secondary text-white px-8 py-4 rounded-xl text-lg font-semibold hover:from-primary/90 hover:to-secondary/90 transition-all duration-300 flex items-center justify-center transform hover:scale-105 shadow-xl hover:shadow-2xl"
                >
                  Start Free Trial
                  <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
                </Link>
                <button className="group border-2 border-primary/30 text-primary px-8 py-4 rounded-xl text-lg font-semibold hover:border-primary hover:bg-primary/5 transition-all duration-300 flex items-center justify-center transform hover:scale-105 bg-white/80 backdrop-blur-sm">
                  <FaPlay className="mr-2 group-hover:scale-110 transition-transform duration-300" />
                  Watch Demo
                </button>
              </div>

              <div className="flex flex-wrap items-center gap-6 text-sm text-gray-500">
                <div className="flex items-center">
                  <FaCheckCircle className="text-primary mr-2" />
                  No credit card required
                </div>
                <div className="flex items-center">
                  <FaCheckCircle className="text-primary mr-2" />
                  14-day free trial
                </div>
                <div className="flex items-center">
                  <FaCheckCircle className="text-primary mr-2" />
                  Cancel anytime
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white/80 backdrop-blur-sm rounded-3xl shadow-2xl p-8 border border-white/20">
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">Dashboard Overview</h3>
                    <div className="flex space-x-2">
                      <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                      <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                      <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-gradient-to-br from-primary/10 to-primary/5 p-4 rounded-xl border border-primary/20">
                      <div className="text-2xl font-bold text-primary">24</div>
                      <div className="text-sm text-gray-600">Active Orders</div>
                    </div>
                    <div className="bg-gradient-to-br from-secondary/10 to-secondary/5 p-4 rounded-xl border border-secondary/20">
                      <div className="text-2xl font-bold text-secondary">₹45K</div>
                      <div className="text-sm text-gray-600">Today's Revenue</div>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                      <span className="text-sm font-medium">Wedding Catering - Mumbai</span>
                      <span className="text-xs bg-gradient-to-r from-yellow-100 to-orange-100 text-yellow-800 px-3 py-1 rounded-full border border-yellow-200">Pending</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                      <span className="text-sm font-medium">Corporate Event - Delhi</span>
                      <span className="text-xs bg-gradient-to-r from-primary/10 to-primary/5 text-primary px-3 py-1 rounded-full border border-primary/20">Completed</span>
                    </div>
                    <div className="flex items-center justify-between p-3 bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl border border-gray-200">
                      <span className="text-sm font-medium">Birthday Party - Bangalore</span>
                      <span className="text-xs bg-gradient-to-r from-secondary/10 to-secondary/5 text-secondary px-3 py-1 rounded-full border border-secondary/20">In Progress</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating elements */}
              <div className="absolute -top-4 -right-4 bg-white/90 backdrop-blur-sm rounded-xl shadow-lg p-3 border border-white/20">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-gradient-to-r from-primary to-secondary rounded-full animate-pulse"></div>
                  <span className="text-xs font-medium text-gray-600">Live Orders</span>
                </div>
              </div>
              
              <div className="absolute -bottom-4 -left-4 bg-white/90 backdrop-blur-sm rounded-xl shadow-lg p-3 border border-white/20">
                <div className="flex items-center space-x-2">
                  <FaStar className="text-yellow-400 text-sm" />
                  <span className="text-xs font-medium text-gray-600">4.9/5 Rating</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gradient-to-br from-primary/5 to-secondary/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center group">
                <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${stat.gradient} rounded-2xl mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                  <div className="text-white">{stat.icon}</div>
                </div>
                <div className={`text-4xl font-bold ${index % 2 === 0 ? 'text-primary' : 'text-secondary'} mb-2`}>{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-full text-sm font-medium text-primary border border-primary/20 mb-4">
              <FaGlobe className="w-4 h-4 mr-2" />
              Everything You Need
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Manage Your Catering Business
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our comprehensive platform provides all the tools you need to streamline operations, 
              manage orders, and grow your catering business with style.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="group bg-white p-8 rounded-2xl shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br ${feature.gradient} rounded-2xl mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                  <div className="text-white">{feature.icon}</div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gradient-to-br from-primary/5 to-secondary/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-full text-sm font-medium text-primary border border-primary/20 mb-4">
              <FaHeart className="w-4 h-4 mr-2" />
              Loved by Professionals
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              What Our Customers Say
            </h2>
            <p className="text-xl text-gray-600">
              See what catering professionals have to say about their experience
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white/80 backdrop-blur-sm p-8 rounded-2xl shadow-lg border border-white/20 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FaStar key={i} className="text-yellow-400 text-sm" />
                  ))}
                </div>
                <p className="text-gray-600 mb-6 leading-relaxed">"{testimonial.content}"</p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-full flex items-center justify-center text-white font-semibold mr-4">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-500">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary via-primary/90 to-secondary relative overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
            Ready to Transform Your Business?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Join hundreds of successful catering businesses already using our platform
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/register"
              className="group bg-white text-primary px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-50 transition-all duration-300 flex items-center justify-center transform hover:scale-105 shadow-xl hover:shadow-2xl"
            >
              Start Your Free Trial
              <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
            <Link 
              to="/login"
              className="group border-2 border-white text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-white hover:text-primary transition-all duration-300 flex items-center justify-center transform hover:scale-105 backdrop-blur-sm"
            >
              Sign In
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-secondary to-primary text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mr-3">
                  <FaUtensils className="w-6 h-6 text-white" />
                </div>
                <span className="text-2xl font-bold">Caterers Pro</span>
              </div>
              <p className="text-white/80 mb-6 max-w-md leading-relaxed">
                Professional catering management system designed to streamline your business operations 
                and help you deliver exceptional service to your customers with style and efficiency.
              </p>
              <div className="flex space-x-4">
                <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center hover:scale-110 transition-transform duration-300 cursor-pointer">
                  <FaGlobe className="w-5 h-5" />
                </div>
                <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center hover:scale-110 transition-transform duration-300 cursor-pointer">
                  <FaEnvelope className="w-5 h-5" />
                </div>
                <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center hover:scale-110 transition-transform duration-300 cursor-pointer">
                  <FaPhone className="w-5 h-5" />
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-6">Product</h3>
              <ul className="space-y-3 text-white/80">
                <li><a href="#" className="hover:text-white transition-colors duration-300 hover:translate-x-1 transform inline-block">Features</a></li>
                <li><a href="#" className="hover:text-white transition-colors duration-300 hover:translate-x-1 transform inline-block">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors duration-300 hover:translate-x-1 transform inline-block">Demo</a></li>
                <li><a href="#" className="hover:text-white transition-colors duration-300 hover:translate-x-1 transform inline-block">Support</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold mb-6">Company</h3>
              <ul className="space-y-3 text-white/80">
                <li><a href="#" className="hover:text-white transition-colors duration-300 hover:translate-x-1 transform inline-block">About</a></li>
                <li><a href="#" className="hover:text-white transition-colors duration-300 hover:translate-x-1 transform inline-block">Contact</a></li>
                <li><a href="#" className="hover:text-white transition-colors duration-300 hover:translate-x-1 transform inline-block">Privacy</a></li>
                <li><a href="#" className="hover:text-white transition-colors duration-300 hover:translate-x-1 transform inline-block">Terms</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-white/20 mt-12 pt-8 text-center text-white/80">
            <p>&copy; 2024 Caterers Pro. All rights reserved. Made with ❤️ for catering professionals.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;